/**
 * Application Route Configuration
 * 
 * This file defines all navigation routes for the ShuttleNomix application.
 * Routes are organized into PUBLIC routes (no authentication) and PROTECTED routes 
 * (require authentication via AuthGuard).
 * 
 * ================================================================================
 * ROUTE PROTECTION WITH AuthGuard
 * ================================================================================
 * 
 * PUBLIC ROUTES (No AuthGuard):
 * - /welcome           - Application entry point, splash screen
 * - /login             - User authentication page
 * - /campaignOptout    - Marketing campaign opt-out (public link)
 * - /cancel            - Account cancellation (public link)
 * - /showCampaign      - Display marketing campaign (public link)
 * 
 * PROTECTED ROUTES (With AuthGuard):
 * - All other routes require user authentication
 * - AuthGuard checks if user is logged in before allowing access
 * - Unauthenticated users are redirected to /welcome
 * 
 * ================================================================================
 * NAVIGATION BEST PRACTICES: When to Use replaceUrl
 * ================================================================================
 * 
 * Angular Router's NavigationExtras provides a `replaceUrl` option that controls
 * how navigation affects the browser history and DOM:
 * 
 * ┌─────────────────────────────────────────────────────────────────────────────┐
 * │ replaceUrl: false (DEFAULT)                                                 │
 * ├─────────────────────────────────────────────────────────────────────────────┤
 * │ • Adds new entry to browser history                                         │
 * │ • Keeps previous page in DOM (hidden with aria-hidden="true")              │
 * │ • Back button returns to previous page                                      │
 * │ • Used for normal navigation between app pages                              │
 * │                                                                              │
 * │ Example:                                                                     │
 * │   router.navigateByUrl('/settings')  // User can go back to previous page   │
 * └─────────────────────────────────────────────────────────────────────────────┘
 * 
 * ┌─────────────────────────────────────────────────────────────────────────────┐
 * │ replaceUrl: true                                                            │
 * ├─────────────────────────────────────────────────────────────────────────────┤
 * │ • Replaces current entry in browser history                                 │
 * │ • Removes previous page from DOM completely                                 │
 * │ • Back button skips to page before the replaced one                         │
 * │ • Used for entry points and authentication flows                            │
 * │                                                                              │
 * │ Example:                                                                     │
 * │   router.navigateByUrl('/dashboard', { replaceUrl: true })                  │
 * │   // User cannot return to welcome/login page                               │
 * └─────────────────────────────────────────────────────────────────────────────┘
 * 
 * ================================================================================
 * ✅ USE replaceUrl: true FOR:
 * ================================================================================
 * 
 * 1. AUTHENTICATION FLOWS
 *    • Login → Dashboard
 *    • Welcome → Dashboard (auto-login)
 *    • Signup → Dashboard
 *    
 *    Why: Users shouldn't return to login/welcome after authentication.
 *    Browser back button should exit app or go to previous authenticated page.
 * 
 * 2. ONBOARDING FLOWS
 *    • Tutorial → Home
 *    • Setup Wizard → Dashboard
 *    
 *    Why: Onboarding is one-time. Don't let users go back to incomplete setup.
 * 
 * 3. SPLASH SCREENS
 *    • Splash → Main Page
 *    
 *    Why: Splash screens are temporary. Don't keep in navigation history.
 * 
 * 4. ERROR RECOVERY
 *    • Error Page → Recovered Page
 *    • 404 → Home
 *    
 *    Why: Error pages shouldn't be in history. Navigate away permanently.
 * 
 * 5. FORM SUBMISSIONS
 *    • Form → Success Page
 *    
 *    Why: Prevent accidental form resubmission via back button.
 * 
 * ================================================================================
 * ❌ DO NOT USE replaceUrl: true FOR:
 * ================================================================================
 * 
 * 1. NORMAL NAVIGATION
 *    • Dashboard → Settings
 *    • List → Detail
 *    • Menu item navigation
 *    
 *    Why: Users expect back button to work normally.
 * 
 * 2. DEEP LINKING
 *    • External link → App page
 *    
 *    Why: Preserve navigation history for better UX.
 * 
 * 3. TAB NAVIGATION
 *    • Tab 1 → Tab 2
 *    
 *    Why: Tabs should maintain their own navigation stacks.
 * 
 * 4. MODAL DIALOGS
 *    • Page → Modal → Page
 *    
 *    Why: Modals are overlays, not navigation. Use Ionic modal controller instead.
 * 
 * ================================================================================
 * REAL-WORLD EXAMPLES
 * ================================================================================
 * 
 * // ✅ CORRECT: Login flow (replaceUrl: true)
 * // File: welcome.page.ts
 * async handleAuthenticatedUser(user: User) {
 *   await this.loaderService.prepareSession(user.uid);
 *   await this.router.navigateByUrl('/dashboard', { 
 *     replaceUrl: true  // Remove welcome page from history
 *   });
 * }
 * 
 * // ✅ CORRECT: Menu navigation (replaceUrl: false - default)
 * // File: app.component.ts
 * async onMenuItemClick(url: string) {
 *   await this.router.navigateByUrl(url);  // Keep navigation history
 * }
 * 
 * // ✅ CORRECT: Form submission (replaceUrl: true)
 * // File: ticket3Save.page.ts
 * async saveTicket() {
 *   await this.ticketService.save(this.ticket);
 *   await this.router.navigateByUrl('/tickets', { 
 *     replaceUrl: true  // Prevent back to form after save
 *   });
 * }
 * 
 * // ✅ CORRECT: Detail page navigation (replaceUrl: false - default)
 * // File: tickets.page.ts
 * async viewTicketDetail(ticketId: string) {
 *   await this.router.navigate(['/tickets/ticket1Passenger', ticketId]);
 *   // User can go back to ticket list
 * }
 * 
 * ================================================================================
 * ACCESSIBILITY & ARIA-HIDDEN ISSUE
 * ================================================================================
 * 
 * Without replaceUrl: true on authentication flows, the following issue occurs:
 * 
 * 1. User logs in at /welcome
 * 2. App navigates to /dashboard (without replaceUrl: true)
 * 3. Ionic keeps <app-welcome> in DOM with aria-hidden="true"
 * 4. WelcomePage still has a <button> with focus
 * 5. Browser blocks aria-hidden because focused element inside
 * 6. DashboardPage renders but is visually blocked
 * 
 * Result: User sees blank screen even though navigation succeeded!
 * 
 * Solution: Use replaceUrl: true to completely remove WelcomePage from DOM.
 * 
 * ================================================================================
 * NAVIGATION STACK VISUALIZATION
 * ================================================================================
 * 
 * Without replaceUrl: true:
 * ┌─────────────────────┐
 * │  DashboardPage      │ ← Visible (z-index: 101)
 * ├─────────────────────┤
 * │  WelcomePage        │ ← Hidden (z-index: 100, aria-hidden: true, still in DOM)
 * └─────────────────────┘
 * 
 * With replaceUrl: true:
 * ┌─────────────────────┐
 * │  DashboardPage      │ ← Only page in stack (z-index: 100)
 * └─────────────────────┘
 * 
 * ================================================================================
 * ROUTE STRUCTURE
 * ================================================================================
 * 
 * Routes are organized by feature module:
 * 
 * • Public          - welcome, login, campaign pages
 * • Dashboard       - main application dashboard
 * • About           - version, release notes, terms, privacy
 * • Account         - profile, orders, billing
 * • Help            - contact, tutorials, feedback
 * • Manager         - dealership settings, users, shuttles, service areas
 * • Marketing       - campaigns, customers, incentives
 * • Navigation      - driver route navigation workflow (5 steps)
 * • Reports         - report type, parameters, view
 * • Routes          - route creation workflow (8 steps)
 * • Tickets         - ticket management workflow (3 steps)
 * • Tracker         - live tracking, historical trips/routes
 * • Settings        - user preferences
 * • Modals          - reusable modal pages (address, calendar, checkbox, etc.)
 * 
 * ================================================================================
 * MAINTENANCE NOTES
 * ================================================================================
 * 
 * When adding new routes:
 * 1. Determine if route requires authentication (most do)
 * 2. Add canActivate: [AuthGuard] for protected routes
 * 3. Group routes by feature for maintainability
 * 4. Use consistent naming: feature/subfeature/page
 * 5. Document any special routing behavior in code comments
 * 
 * When implementing navigation in pages:
 * 1. Use replaceUrl: true for entry points (login, welcome, onboarding)
 * 2. Use default (replaceUrl: false) for normal navigation
 * 3. Always handle navigation errors with try/catch
 * 4. Consider user experience: should they be able to go back?
 * 
 * ================================================================================
 */
import { AuthGuard } from '@shared/services';
import type { Routes } from '@angular/router';

// Pages (imported for routing)
import {
    AboutPage, AccessDeniedPage, AccountPage, AccountProfilePage, 
    Apply1WhatYouGetPage, Apply2ApplicationFormPage, Apply3LoginCredentialsPage, Apply4QuickStartPage,
    
    CampaignOptoutPage, CampaignsPage, CampaignPage, CampaignIncentivePage, 
    CampaignIncentiveSetsPage, CampaignIncentiveSetPage, CampaignIncentivesPage,
    CampaignMessagePage, CampaignHostingPage, CampaignFormatSelectorPage,
    
    CustomersPage, CancelPage, ContactUsPage, 
    DashboardPage, DealershipPage, DropoffAllAboardPage, DropoffCompletedPage, FeedbackPage,
    HelpPage, IntroductionPage, LoginPage, 
    
    ManagerSettingsPage, MarketingPage, MessageCenterPage, MessagePage,
    Navigation1ShuttleSelectionPage,
    Navigation2AttendancePage, Navigation3StartRoutePage, Navigation4ControlPanelPage,
    Navigation5CongratulationsPage, 
    
    PickupApproachingPage, PickupArrivedPage, PickupCompletedPage,
    PrivacyPolicyPage, OrderPage, OrdersPage, 
    Report1TypePage, Report2ParametersPage, Report3ViewPage, 
    ReleaseNotesPage,
    RoutesPage, Route1ShuttleSelectionPage, Route2TicketSelectionPage,  Route3LocationsPage,
    Route4OrderingPage, Route5VerificationPage, Route6SavePage, Route7NotificationPage, Route8CongratulationsPage,
     
    ShowCampaignPage, RouteViewPage,
    SatisfactionPage1, SatisfactionPage2, ShuttlePage, ShuttlesPage, 
    ServiceAreasPage, ServiceAreaSinglePage, ServiceAreaMultiplePage, TicketCreatedPage,
    TermsOfServicePage, TicketsPage, Ticket1PassengerPage, Ticket2DestinationPage, Ticket3SavePage, 
    TrackerPage, Tracker1HistoricalFilterPage, Tracker2HistoricalTripsPage,
    Tracker3HistoricalRoutesPage, TrackerLivePage, TutorialsPage, UserPage, UsersPage, UserSettingsPage, 
    VersionPage, WelcomePage
} from '@frontend/pages';
import {
    AddressModalPage, CalendarModalPage, CheckboxModalPage, PickupRequestModalPage,
    PopoverMenuPage, RadioModalPage, TicketsModalPage, VariablesModalPage, NotFoundPage
} from '@shared/pages';

// Define routes

export const routes: Routes = [
    // Public routes - No authentication required
    // { path: '', component: WelcomePage },
    { path: '', redirectTo: '/welcome', pathMatch: 'full' },
    { path: 'apply', redirectTo: '/apply/whatYouGet', pathMatch: 'full' },

    { path: 'welcome', component: WelcomePage },
    { path: 'login', component: LoginPage },
    { path: 'apply/whatYouGet', component: Apply1WhatYouGetPage },
    { path: 'apply/applicationForm', component: Apply2ApplicationFormPage },
    { path: 'apply/loginCredentials', component: Apply3LoginCredentialsPage },
    { path: 'apply/quickStart', component: Apply4QuickStartPage },
    { path: 'apply/campaignOptout', component: CampaignOptoutPage },
    { path: 'apply/cancel', component: CancelPage },
    { path: 'apply/showCampaign', component: ShowCampaignPage },

    // Protected routes - Authentication required
    { path: 'about', component: AboutPage, canActivate: [AuthGuard] },
    { path: 'about/version', component: VersionPage, canActivate: [AuthGuard] },
    { path: 'about/releaseNotes', component: ReleaseNotesPage, canActivate: [AuthGuard] },
    { path: 'about/termsOfService', component: TermsOfServicePage, canActivate: [AuthGuard] },
    { path: 'about/privacyPolicy', component: PrivacyPolicyPage, canActivate: [AuthGuard] },
    { path: 'accessDenied', component: AccessDeniedPage, canActivate: [AuthGuard] },

    { path: 'account', component: AccountPage, canActivate: [AuthGuard] },
    { path: 'account/accountProfile', component: AccountProfilePage, canActivate: [AuthGuard] },
    { path: 'account/orders', component: OrdersPage, canActivate: [AuthGuard] },
    { path: 'account/order', component: OrderPage, canActivate: [AuthGuard] },

    { path: 'addressModal', component: AddressModalPage, canActivate: [AuthGuard] },
    { path: 'calendarModal', component: CalendarModalPage, canActivate: [AuthGuard] },
    { path: 'checkboxModal', component: CheckboxModalPage, canActivate: [AuthGuard] },
    { path: 'dashboard', component: DashboardPage, canActivate: [AuthGuard] },

    { path: 'help', component: HelpPage, canActivate: [AuthGuard] },
    { path: 'help/contactUs', component: ContactUsPage, canActivate: [AuthGuard] },
    { path: 'help/introduction', component: IntroductionPage, canActivate: [AuthGuard] },
    { path: 'help/tutorials', component: TutorialsPage, canActivate: [AuthGuard] },
    { path: 'help/feedback', component: FeedbackPage, canActivate: [AuthGuard] },
    { path: 'help/messageCenter', component: MessageCenterPage, canActivate: [AuthGuard] },
    { path: 'help/messageCenter/message/:uid', component: MessagePage, canActivate: [AuthGuard] },
    
    { path: 'managerSettings', component: ManagerSettingsPage, canActivate: [AuthGuard] },
    { path: 'managerSettings/dealership', component: DealershipPage, canActivate: [AuthGuard] },
    { path: 'managerSettings/users', component: UsersPage, canActivate: [AuthGuard] },
    { path: 'managerSettings/users/user', component: UserPage, canActivate: [AuthGuard] },
    { path: 'managerSettings/users/user/:uid', component: UserPage, canActivate: [AuthGuard] },
    { path: 'managerSettings/shuttles', component: ShuttlesPage, canActivate: [AuthGuard] },
    { path: 'managerSettings/shuttles/shuttle', component: ShuttlePage, canActivate: [AuthGuard] },
    { path: 'managerSettings/shuttles/shuttle/:uid', component: ShuttlePage, canActivate: [AuthGuard] },
    { path: 'managerSettings/serviceAreas', component: ServiceAreasPage, canActivate: [AuthGuard] },
    { path: 'managerSettings/serviceAreas/serviceAreaSingle/:uid', component: ServiceAreaSinglePage, canActivate: [AuthGuard] },
    { path: 'managerSettings/serviceAreas/serviceAreaMultiple', component: ServiceAreaMultiplePage, canActivate: [AuthGuard] },

    { path: 'managerSettings/ticketCreated', component: TicketCreatedPage, canActivate: [AuthGuard] },
    { path: 'managerSettings/dropoffAllAboard', component: DropoffAllAboardPage, canActivate: [AuthGuard] },
    { path: 'managerSettings/dropoffCompleted', component: DropoffCompletedPage, canActivate: [AuthGuard] },
    { path: 'managerSettings/pickupApproaching', component: PickupApproachingPage, canActivate: [AuthGuard] },
    { path: 'managerSettings/pickupArrived', component: PickupArrivedPage, canActivate: [AuthGuard] },
    { path: 'managerSettings/pickupCompleted', component: PickupCompletedPage, canActivate: [AuthGuard] },

    { path: 'marketing', component: MarketingPage, canActivate: [AuthGuard] },
    { path: 'marketing/campaigns', component: CampaignsPage, canActivate: [AuthGuard] },
    { path: 'marketing/campaigns/campaign', component: CampaignPage, canActivate: [AuthGuard] },
    { path: 'marketing/customers', component: CustomersPage, canActivate: [AuthGuard] },
    { path: 'marketing/campaignFormatSelector', component: CampaignFormatSelectorPage, canActivate: [AuthGuard] },
    { path: 'marketing/campaignHosting', component: CampaignHostingPage, canActivate: [AuthGuard] },
    { path: 'marketing/campaignIncentiveSet', component: CampaignIncentiveSetPage, canActivate: [AuthGuard] },
    { path: 'marketing/campaignIncentiveSets', component: CampaignIncentiveSetsPage, canActivate: [AuthGuard] },
    { path: 'marketing/campaignIncentive', component: CampaignIncentivePage, canActivate: [AuthGuard] },
    { path: 'marketing/campaignIncentive/:uid', component: CampaignIncentivePage, canActivate: [AuthGuard] },
    { path: 'marketing/campaignIncentives', component: CampaignIncentivesPage, canActivate: [AuthGuard] },
    { path: 'marketing/campaignMessage', component: CampaignMessagePage, canActivate: [AuthGuard] },

    { path: 'navigation', component: Navigation1ShuttleSelectionPage, canActivate: [AuthGuard] },
    { path: 'navigation/navigation1ShuttleSelection', component: Navigation1ShuttleSelectionPage, canActivate: [AuthGuard] },
    { path: 'navigation/navigation2Attendance', component: Navigation2AttendancePage, canActivate: [AuthGuard] },
    { path: 'navigation/navigation3StartRoute', component: Navigation3StartRoutePage, canActivate: [AuthGuard] },
    { path: 'navigation/navigation4ControlPanel', component: Navigation4ControlPanelPage, canActivate: [AuthGuard] },
    { path: 'navigation/navigation5Congratulations', component: Navigation5CongratulationsPage, canActivate: [AuthGuard] },
    { path: 'pickupRequestModal', component: PickupRequestModalPage, canActivate: [AuthGuard] },
    { path: 'popoverMenu', component: PopoverMenuPage, canActivate: [AuthGuard] },
    { path: 'radioModal', component: RadioModalPage, canActivate: [AuthGuard] },

    { path: 'reports/report1Type', component: Report1TypePage, canActivate: [AuthGuard] },
    { path: 'reports/report2Parameters', component: Report2ParametersPage, canActivate: [AuthGuard] },
    { path: 'reports/report3View', component: Report3ViewPage, canActivate: [AuthGuard] },

    { path: 'report1TypePage', component: Report1TypePage, canActivate: [AuthGuard] },
    { path: 'report2Parameters', component: Report2ParametersPage, canActivate: [AuthGuard] },
    { path: 'report3View', component: Report3ViewPage, canActivate: [AuthGuard] },
    
    { path: 'routes', component: RoutesPage, canActivate: [AuthGuard] },
    { path: 'routes/routeView', component: RouteViewPage, canActivate: [AuthGuard] },
    { path: 'routes/route1ShuttleSelection', component: Route1ShuttleSelectionPage, canActivate: [AuthGuard] },
    { path: 'routes/route2TicketSelection', component: Route2TicketSelectionPage, canActivate: [AuthGuard] },
    { path: 'routes/route3Locations', component: Route3LocationsPage, canActivate: [AuthGuard] },
    { path: 'routes/route4Ordering', component: Route4OrderingPage, canActivate: [AuthGuard] },
    { path: 'routes/route5Verification', component: Route5VerificationPage, canActivate: [AuthGuard] },
    { path: 'routes/route6Save', component: Route6SavePage, canActivate: [AuthGuard] },
    { path: 'routes/route7Notification', component: Route7NotificationPage, canActivate: [AuthGuard] },
    { path: 'routes/route8Congratulations', component: Route8CongratulationsPage, canActivate: [AuthGuard] },

    // { path: 'releaseNotes', component: ReleaseNotesPage, canActivate: [AuthGuard] },
    { path: 'satisfaction1', component: SatisfactionPage1, canActivate: [AuthGuard] },
    { path: 'satisfaction2', component: SatisfactionPage2, canActivate: [AuthGuard] },

    { path: 'tickets', component: TicketsPage, canActivate: [AuthGuard] },
    { path: 'tickets/ticket1Passenger', component: Ticket1PassengerPage, canActivate: [AuthGuard] },
    { path: 'tickets/ticket1Passenger/:uid', component: Ticket1PassengerPage, canActivate: [AuthGuard] },
    { path: 'tickets/ticket2Destination', component: Ticket2DestinationPage, canActivate: [AuthGuard] },
    { path: 'tickets/ticket3Save', component: Ticket3SavePage, canActivate: [AuthGuard] },
    { path: 'ticketsModal', component: TicketsModalPage, canActivate: [AuthGuard] },

    { path: 'tracker', component: TrackerPage, canActivate: [AuthGuard] },
    { path: 'tracker/trackerLive', component: TrackerLivePage, canActivate: [AuthGuard] },
    { path: 'tracker/tracker1HistoricalFilter', component: Tracker1HistoricalFilterPage, canActivate: [AuthGuard] },
    { path: 'tracker/tracker2HistoricalTrips', component: Tracker2HistoricalTripsPage, canActivate: [AuthGuard] },
    { path: 'tracker/tracker3HistoricalRoutes', component: Tracker3HistoricalRoutesPage, canActivate: [AuthGuard] },

    // { path: 'tracker/trackerLive/:ai/:sbb', component: TrackerLivePage, canActivate: [AuthGuard] },

    { path: 'variablesModal', component: VariablesModalPage, canActivate: [AuthGuard] },
    { path: 'userSettings', component: UserSettingsPage, canActivate: [AuthGuard] },

    // Catch-all route for undefined paths
    { path: '**', component: NotFoundPage }
];
